import React, { useMemo } from 'react';
import {
	AccordionDetails,
	AccordionSummary,
	Collapse,
	FormLabel,
	Link,
	Stack,
	Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useObjectContext } from '@/hooks/useObjectContext';
import { paths } from '@/app/routes';
import { useHits } from 'react-instantsearch';
import { getUrlFromPipeline } from '@/utils/constants';
import RenderTask from './RenderTask';

const TaskGroupContent = ({ expanded, displayCard, ...props }) => {
	const { results, items } = useHits(props);
	const tasks = items ?? [];
	const { dealRow, dealId, clientId, relationshipCompanyId } = useObjectContext();
	const navigate = useNavigate();
	const location = useLocation();
	const pipeline = dealRow?.pipeline;
	const total = results?.nbHits ?? 0;
	const shownTasks = tasks.slice(0, 3);

	const tasksUrl = useMemo(() => {
		if (relationshipCompanyId) {
			return `${paths.companies}/${relationshipCompanyId}/tasks`;
		}
		if (dealId && pipeline) {
			return `${getUrlFromPipeline(pipeline)}/${dealId}/tasks`;
		}
		if (clientId) {
			const basePath = location.pathname.startsWith(paths.prospects)
				? paths.prospects
				: paths.clients;
			return `${basePath}/${clientId}/tasks`;
		}
		return '/';
	}, [pipeline, dealId, clientId, relationshipCompanyId, location.pathname]);

	const handleViewAllClick = (e) => {
		e.preventDefault();
		navigate(tasksUrl);
	};

	if (!dealId && !clientId && !relationshipCompanyId) return null;

	return (
		<>
			<AccordionSummary
				expandIcon={
					total > 0 ? (
						<ExpandMore sx={(theme) => ({ color: theme.palette.primary.main })} />
					) : (
						<></>
					)
				}
				sx={{
					padding: 0,
					'& .MuiAccordionSummary-content': {
						marginRight: 2,
					},
				}}
			>
				<Stack
					direction='row'
					justifyContent={'space-between'}
					alignItems={'center'}
					sx={{ width: '100%' }}
				>
					<FormLabel
						sx={{
							mt: 2,
							fontSize: '0.875rem',
							fontWeight: 600,
							display: 'block',
						}}
					>
						{'Recent tasks'}
					</FormLabel>
					{total > 0 && (
						<Link
							to={tasksUrl}
							component={RouterLink}
							sx={{ textDecorationLine: 'none', paddingTop: '2px' }}
							onClick={handleViewAllClick}
						>
							View all
						</Link>
					)}
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={{ padding: '0px' }}>
				<TransitionGroup>
					{shownTasks.length > 0 ? (
						shownTasks.map((task) => (
							<RenderTask
								key={`task-${task.id}`}
								{...{ task, expanded, displayCard }}
							/>
						))
					) : (
						<Collapse in={true}>
							<Typography variant='task_updated'>No incomplete tasks</Typography>
						</Collapse>
					)}
				</TransitionGroup>
			</AccordionDetails>
		</>
	);
};
TaskGroupContent.displayName = 'TaskGroupContent';

export default TaskGroupContent;
